import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import type { TransformedCart } from "../../cart/types/cartApiTypes";
import { useRemoveCoupon } from "../../cart/hooks/useRemoveCoupon";
import Icon from "../../../../common/components/Icon/Icon";

interface CheckoutOrderSummaryProps {
  cart: TransformedCart;
}

const CheckoutOrderSummary: React.FC<CheckoutOrderSummaryProps> = ({
  cart,
}) => {
  const { t } = useTranslation();
  const { totals, coupons } = cart;
  const [removingCode, setRemovingCode] = useState<string | null>(null);
  const { handleRemoveCoupon: triggerRemoveCoupon, isRemoving } =
    useRemoveCoupon();

  useEffect(() => {
    if (!isRemoving) {
      setRemovingCode(null);
    }
  }, [isRemoving]);

  const handleRemoveCoupon = (code: string) => {
    if (isRemoving) return;
    setRemovingCode(code);
    triggerRemoveCoupon({ code });
  };

  return (
    <div className="checkout-summary">
      <h3 className="checkout-summary__title">
        {t("cartTotals.title", "Order Summary")}
      </h3>

      {/* Applied Coupons */}
      {coupons && coupons.length > 0 && (
        <div className="checkout-summary__coupons">
          <ul className="checkout-summary__coupons-list">
            {coupons.map((coupon) => {
              const isThisOneRemoving =
                isRemoving && removingCode === coupon.code;
              return (
                <li key={coupon.code} className="checkout-summary__coupon-item">
                  <span className="coupon-code">
                    <Icon iconName="ticket-outlined" /> {coupon.code}
                  </span>
                  <div className="coupon-actions">
                    <span className="coupon-discount">
                      -{coupon.totals.displayDiscount}
                    </span>
                    <button
                      className="coupon-remove-btn"
                      onClick={() => handleRemoveCoupon(coupon.code)}
                      disabled={isThisOneRemoving}
                      aria-label={t("cartTotals.appliedCoupons.removeLabel", {
                        code: coupon.code,
                      })}
                    >
                      {isThisOneRemoving ? (
                        <div className="loader-spinner" />
                      ) : (
                        <span>&times;</span>
                      )}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="checkout-summary__rows">
        {/* 1. Subtotal (Ara Toplam) */}
        <div className="checkout-summary__row">
          <span>{t("cartTotals.subtotal", "Subtotal")}</span>
          <span>{totals.totalItems}</span>
        </div>

        {/* 2. Tax (Vergiler) */}
        {totals.hasTax && (
          <div className="checkout-summary__row">
            <span>{t("cartTotals.tax", "Tax")}</span>
            <span>{totals.totalTax}</span>
          </div>
        )}

        {/* 3. Shipping (Gönderim Ücreti) */}
        {/* Conditional Render: Only show if cart needs shipping and shipping has been calculated */}
        {cart.needsShipping && cart.hasCalculatedShipping && (
          <div className="checkout-summary__row">
            <span>{t("cartTotals.shipping", "Shipping")}</span>
            <span
              className={totals.shippingStatus === "free" ? "is-success" : ""}
            >
              {
                {
                  free: t("cartTotals.freeShipping", "Free Shipping!"),
                  pickup: t("cartTotals.localPickup", "Local Pickup"),
                  paid: totals.totalShipping,
                  none: t(
                    "cartTotals.noShippingOptions",
                    "No shipping options available.",
                  ),
                }[totals.shippingStatus]
              }
            </span>
          </div>
        )}

        {/* 4. Fees */}
        {totals.hasFees && (
          <div className="checkout-summary__row">
            <span>{t("cartTotals.fees", "Fees")}</span>
            <span>{totals.totalFees}</span>
          </div>
        )}

        {/* 5. Discount (İndirimler) */}
        {totals.hasDiscount && (
          <div className="checkout-summary__row">
            <span>{t("cartTotals.discount", "Discount")}</span>
            <span className="is-discount">-{totals.totalDiscount}</span>
          </div>
        )}
      </div>

      {/* Grand Total */}
      <div className="checkout-summary__total">
        <span>{t("cartTotals.total", "Total")}</span>
        <span>{totals.totalPrice}</span>
      </div>
    </div>
  );
};

export default CheckoutOrderSummary;
